import clsx from "clsx";
import { memo, useEffect, useRef, useState } from "react";
import noImage from "@/assets/img/no-image.svg";
import { isR18ThreadTagName, resolveUploadPath } from "@/entities/thread";
import { getArchiveLinkAttributes } from "@/features/archive/archiveCatalogBehavior";
import type { ArchiveThread } from "@/features/archive/types";
import { CatalogBoardIcon } from "@/features/catalog/components/CatalogBoardIcon";
import { CatalogThreadLink } from "@/features/catalog/components/navigation/CatalogThreadLink";
import { useCatalogStore } from "@/features/catalog/stores/catalogStore";
import { useSettingsStore } from "@/features/settings/hooks";
import { formatDateToMinute } from "@/shared/lib/date";

interface ArchiveItemProps {
  thread: ArchiveThread;
  openInNewTab?: boolean;
}

export const ArchiveItem: React.FunctionComponent<ArchiveItemProps> = memo(
  function ArchiveItem({ thread, openInNewTab }: ArchiveItemProps) {
    const title = thread.opExcerpt || "(無題)";
    const catalogOpenInNewTab = useCatalogStore((state) => state.openInNewTab);
    const textLines = useCatalogStore((state) => state.textLines);
    const showR18 = useSettingsStore((state) => state.showR18);
    const [r18Revealed, setR18Revealed] = useState(false);
    const linkRef = useRef<HTMLAnchorElement>(null);
    // 既存のgetImageUrlと同様、空文字のサムネイルも本画像へフォールバックする
    const imageUrl = resolveUploadPath(thread.thumbnailUrl || "");
    const isR18 = thread.tags.some((tag) => isR18ThreadTagName(tag.name));
    const isR18Hidden =
      isR18 && Boolean(thread.thumbnailUrl) && !showR18 && !r18Revealed;
    const linkAttributes = getArchiveLinkAttributes(
      openInNewTab ?? catalogOpenInNewTab,
    );
    const archivedAtLabel = formatDateToMinute(thread.archivedAt);

    useEffect(() => {
      if (r18Revealed) linkRef.current?.focus();
    }, [r18Revealed]);

    return (
      <article
        aria-label={title}
        className="group relative bg-card/50 rounded-lg overflow-hidden hover:bg-card transition-colors"
      >
        {isR18Hidden && (
          <button
            type="button"
            className="archive-r18-spoiler-trigger"
            aria-label={`${title}のR18画像を表示`}
            onClick={(): void => setR18Revealed(true)}
          />
        )}
        <CatalogThreadLink
          ref={linkRef}
          thread={{ id: thread.threadId, b: thread.b }}
          {...linkAttributes}
          className="block focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset"
        >
          <figure className="relative aspect-square bg-muted flex items-center justify-center">
            <img
              src={imageUrl}
              alt={title}
              loading="lazy"
              className={clsx(
                "w-full h-full object-contain",
                isR18Hidden && "r18-spoiler-image",
              )}
              onError={(e: React.SyntheticEvent<HTMLImageElement>): void => {
                (e.target as HTMLImageElement).src = noImage;
              }}
            />
            <data
              value={thread.replyCount}
              className="absolute bottom-1 right-1 px-1.5 py-0.5 text-xs font-bold bg-black/70 text-white rounded"
            >
              {thread.replyCount}レス
            </data>
          </figure>
          <header
            className={clsx(
              "flex flex-col px-2",
              textLines > 0 ? "gap-1 py-2" : "py-1",
            )}
          >
            {textLines > 0 && (
              <p
                className="catalog-text-clamp min-w-0 text-xs text-muted-foreground leading-tight transition-colors group-hover:text-foreground"
                style={
                  {
                    "--catalog-text-lines": textLines,
                  } as React.CSSProperties
                }
              >
                {title}
              </p>
            )}
            <time
              dateTime={thread.archivedAt}
              className="archive-saved-at text-muted-foreground truncate"
            >
              {archivedAtLabel}
            </time>
            <CatalogBoardIcon slug={thread.b} />
          </header>
        </CatalogThreadLink>
      </article>
    );
  },
);
